import { useState, useEffect } from 'react'
import FormModal from '../FormModal'
import AutoTextarea from '../AutoTextarea'
import RevealsEditor from './RevealsEditor'
import { parseSections, serializeSections } from '../../utils/descriptionSections'

const formStyles = {
  field: {
    marginBottom: '0.9rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--field-bg)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
}

// Stored in DB as a JSON array string (e.g. '["Jarl","Fiskrbak"]') for historical reasons,
// but the form accepts/shows a plain comma-separated string for easier editing.
const jsonArrayToCommaString = (raw) => {
  if (!raw) return ''
  try {
    const arr = JSON.parse(raw)
    if (Array.isArray(arr)) return arr.join(', ')
  } catch {
    // Not valid JSON — fall through and return the raw value so existing bad data is still editable
  }
  return raw
}

const commaStringToJsonArray = (input) => {
  const trimmed = (input || '').trim()
  if (!trimmed) return ''
  const arr = trimmed.split(',').map(t => t.trim()).filter(Boolean)
  return JSON.stringify(arr)
}

function NPCForm({ isOpen, onClose, npc = null, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    full_description: '',
    avatar_initials: '',
    tags: '',
    is_visible: 0,
    entity_type: 'npc',
    description_sections: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [deleteAvatar, setDeleteAvatar] = useState(false)

  // Update form data when npc prop changes
  useEffect(() => {
    if (npc) {
      setFormData({
        name: npc.name || '',
        title: npc.title || '',
        description: npc.description || '',
        full_description: npc.full_description || '',
        avatar_initials: npc.avatar_initials || '',
        tags: jsonArrayToCommaString(npc.tags),
        is_visible: npc.is_visible || 0,
        entity_type: npc.entity_type || 'npc',
        description_sections: parseSections(npc.description_sections),
      })
      // Set existing avatar preview if available
      setAvatarPreview(npc.avatar_url || null)
      setAvatarFile(null)
      setDeleteAvatar(false)
    } else {
      setFormData({
        name: '',
        title: '',
        description: '',
        full_description: '',
        avatar_initials: '',
        tags: '',
        is_visible: 0,
        entity_type: 'npc',
        description_sections: [],
      })
      setAvatarPreview(null)
      setAvatarFile(null)
      setDeleteAvatar(false)
    }
  }, [npc])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setAvatarFile(file)
      setDeleteAvatar(false) // Clear delete flag when uploading new file
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    setDeleteAvatar(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Drop blank reveal blocks and serialize before they leave the form —
      // the API only touches description_sections when the key is present,
      // so it must always ride along, even when the DM never opened the editor.
      const cleanedSections = formData.description_sections.filter((s) => s.body.trim() !== '')
      await onSubmit(
        { ...formData, tags: commaStringToJsonArray(formData.tags), description_sections: serializeSections(cleanedSections) },
        avatarFile,
        deleteAvatar
      )
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={null}
      isSubmitting={isSubmitting}
    >
      {/* Header row: Name / Title inline on the left, type + visibility stacked in a fixed column on the right */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', flex: 1, minWidth: '220px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={formStyles.label} htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={formStyles.input}
              placeholder="Enter NPC name"
            />
          </div>

          <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={formStyles.label} htmlFor="title">Title / Role</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              style={formStyles.input}
              placeholder="e.g., Lord Protector"
            />
          </div>
        </div>

        <div style={{ width: '196px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div
            role="group"
            aria-label="Entity type"
            style={{
              width: '196px',
              display: 'flex',
              background: 'var(--field-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '999px',
              padding: '2px',
              gap: '2px',
              boxSizing: 'border-box',
            }}
          >
            {[['npc', '👤 NPC'], ['faction', '🏛️ Faction']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, entity_type: value }))}
                style={{
                  flex: 1,
                  padding: '0.4rem 0.3rem',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  background: formData.entity_type === value ? 'var(--active-bg)' : 'transparent',
                  color: formData.entity_type === value ? 'var(--text-light)' : 'var(--text-muted)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, is_visible: prev.is_visible === 1 ? 0 : 1 }))}
            aria-pressed={formData.is_visible === 1}
            style={{
              width: '196px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: formData.is_visible === 1 ? 'var(--active-bg)' : 'var(--field-bg)',
              color: formData.is_visible === 1 ? 'var(--text-light)' : 'var(--text-muted)',
              fontSize: '0.82rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {formData.is_visible === 1 ? '👁️ Visible to players' : '🙈 Hidden from players'}
          </button>
        </div>
      </div>

      {/* Portrait (click to upload) + Tags on the same row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ position: 'relative', width: '56px', height: '56px', flexShrink: 0 }}>
          <label htmlFor="avatar" className="npc-form-portrait" style={{ display: 'block', width: '56px', height: '56px', cursor: 'pointer' }}>
            <div
              className="npc-form-portrait-circle"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid var(--accent-teal)',
                background: 'var(--field-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Portrait preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 700 }}>??</span>
              )}
            </div>
            <span
              className="npc-form-portrait-badge"
              style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'var(--accent-teal-dark)',
                border: '2px solid var(--card-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem',
                lineHeight: 1,
              }}
            >
              📷
            </span>
            <input
              type="file"
              id="avatar"
              name="avatar"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          </label>
          {avatarPreview && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleRemoveAvatar()
              }}
              title="Remove portrait"
              style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                background: '#c0392b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={formStyles.label} htmlFor="tags">Tags</label>
          <input
            type="text"
            id="tags"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            style={formStyles.input}
            placeholder="Quest Giver, Ally, Merchant"
          />
        </div>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="description">
          Card blurb *
          <span className="npc-form-info" tabIndex={0}>
            i
            <span className="npc-form-info-tooltip">
              <span>Card blurb — the short line shown on the NPC&apos;s card in the grid.</span>
              <span>Full description — the longer writeup shown when the card is opened.</span>
              <span>Reveals — extra blocks you can hide until you reveal them; players never see a hidden reveal, or any sign one exists.</span>
              <span>All three grow to fit as you type.</span>
            </span>
          </span>
        </label>
        <AutoTextarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          placeholder="Brief description shown on the NPC card"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="full_description">Full Description</label>
        <AutoTextarea
          id="full_description"
          name="full_description"
          value={formData.full_description}
          onChange={handleChange}
          placeholder="Detailed description shown when clicking on the NPC card"
        />
      </div>

      <RevealsEditor
        sections={formData.description_sections}
        onChange={(next) => setFormData((prev) => ({ ...prev, description_sections: next }))}
      />

      <style>{`
        .npc-form-portrait:hover .npc-form-portrait-circle {
          filter: brightness(1.15);
        }

        .npc-form-info {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 15px;
          height: 15px;
          margin-left: 0.4rem;
          border-radius: 50%;
          border: 1px solid var(--text-muted);
          color: var(--text-muted);
          font-size: 0.65rem;
          font-style: italic;
          font-family: Georgia, serif;
          cursor: help;
          position: relative;
          vertical-align: middle;
        }

        .npc-form-info-tooltip {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          position: absolute;
          left: 0;
          top: calc(100% + 8px);
          width: 260px;
          padding: 0.65rem 0.75rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          font-size: 0.78rem;
          font-style: normal;
          font-family: inherit;
          font-weight: 400;
          line-height: 1.4;
          cursor: auto;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.15s ease;
          z-index: 5;
          pointer-events: none;
        }

        .npc-form-info:hover .npc-form-info-tooltip,
        .npc-form-info:focus .npc-form-info-tooltip {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
    </FormModal>
  )
}

export default NPCForm
